import type { CohortBucket, CohortSignal } from "../api/types";
import { zTone, zWord } from "./zscore";

/**
 * Разбор теневой ставки по перевесу группы «плюсовых» для экрана.
 *
 * # Что этот раздел обязан сказать
 *
 * Панель участников показывает перевес ГРУППЫ с 2026-08-12 и на этом
 * останавливается. Вопрос оператора — стоит ли вставать туда же, когда перевес
 * перевалил за пару тысяч, — упирается в три ловушки, и раздел обязан закрыть
 * все три, а не показать прибыль.
 *
 *   - ПРОТИВ ЧЕГО судить. Доля угаданного сама по себе не значит ничего: она
 *     обязана сравниваться с ценой входа (сколько правда стоила) и отдельно с
 *     физикой (не купили ли мы то, что и так выигрывало). Первое сравнение
 *     закрывает вопрос «есть ли кромка», второе — «есть ли она СВЕРХ знака
 *     „цена против страйка“», у которого своя планка на каждой минуте окна:
 *     64–65 % на первой, 69–70 % на второй, 80 % на третьей, 91–92 % на
 *     четвёртой.
 *   - ГРУППА ЛИ ЭТО. Перевес, который целиком дал один кошелёк, — это
 *     копирование кита под другим именем. Медианная доля крупнейшего участника
 *     стоит в приговоре раздела, а не в сноске.
 *   - НАЛИЛИ ЛИ БЫ. Мейкерская гипотеза без лестницы входа не судится вовсе:
 *     живой замер копирования дал фил 43/43 у проигравших против 38/47 у
 *     выигравших (p = 0.002).
 *
 * # Чего в разделе НЕТ и не будет
 *
 * Кнопок. У подсистемы нет порта постановки ордера — не «выключен флагом», а
 * нет в зависимостях вовсе, — и кнопка по строке, за которой не может стоять
 * ордер, обещала бы действие, которого не существует.
 */

/** Число из строки контракта. null означает прочерк, а не ноль. */
function num(v: string | null | undefined): number | null {
  if (v === null || v === undefined || v === "") return null;
  const n = Number(v);
  return Number.isFinite(n) ? n : null;
}

/** Доля, посчитанная только при непустом знаменателе. Прочерк — «не из чего». */
export function ratio(ok: number, n: number): string {
  if (n <= 0) return "—";
  return `${Math.round((100 * ok) / n)} %`;
}

/** Деньги без копеек: перевес меряется тысячами, и центы в нём — шум. */
export function usd(v: string | null | undefined): string {
  const n = num(v);
  if (n === null) return "—";
  return `$${Math.round(n).toLocaleString("ru-RU")}`;
}

/**
 * Теневой счёт со знаком и с копейками.
 *
 * В отличие от перевеса (usd), здесь центы значат: ставка идёт на $25, и
 * счёт в $1.51 на строку — это ровно тот масштаб, на котором комиссия решает
 * исход. Прочерк при нулевом знаменателе: у ведра без налитых заявок
 * мейкерского счёта не существует, и ноль читался бы как измеренный.
 */
export function pnlWord(v: string | null | undefined, priced: number): string {
  if (priced <= 0) return "—";
  const n = num(v);
  if (n === null) return "—";
  const sign = n > 0 ? "+" : n < 0 ? "−" : "";
  return `${sign}$${Math.abs(n).toFixed(2)}`;
}

/**
 * Цвет счёта. Красится САМ ЗНАК, а не запас в сигмах, и это осознанно: счёт
 * здесь — контрольная величина, отвечающая «сошлось ли с деньгами», а
 * утверждение о кромке делают z-оценки левее. Покрась мы счёт по той же планке
 * в две сигмы, две разные меры выглядели бы одной.
 */
export function pnlTone(v: string | null | undefined, priced: number): string | undefined {
  if (priced <= 0) return undefined;
  const n = num(v);
  if (n === null || n === 0) return undefined;
  return n > 0 ? "var(--color-bid)" : "var(--color-ask)";
}

/**
 * Ступень лесенки словами: «400» на экране ничего не говорит.
 *
 * Проценты ОБОРОТА ОКНА, а не доллары, и это не оформление: $2000 — это 3.8 %
 * оборота медианного окна BTC 5m и 46 % оборота ETH 5m, то есть одно и то же
 * число долларов означало на двух рынках разные события.
 */
export function stepLabel(bp: number): string {
  if (bp <= 0) return "старый порог";
  return `${(bp / 100).toFixed(bp % 100 === 0 ? 0 : 1)} % оборота`;
}

/**
 * Ступень в деньгах по медианному обороту окна: то же число, читаемое кошельком.
 *
 * Прочерк, когда оборота не знаем: подставить сюда ноль значило бы объявить
 * ступень бесплатной.
 */
export function stepUSD(b: CohortBucket): string {
  const gross = num(b.window_gross_median);
  if (gross === null || gross <= 0) return "—";
  return usd(String((gross * b.threshold_bp) / 10000));
}

/** Минута окна словами: «0-60» на экране ничего не говорит. */
export function bucketLabel(bucket: string): string {
  switch (bucket) {
    case "0-60":
      return "последняя минута";
    case "60-120":
      return "за 1–2 мин";
    case "120-180":
      return "за 2–3 мин";
    case "180+":
      return "раньше 3 мин";
    default:
      return bucket;
  }
}

// Показ z-оценки переехал в общий модуль: тем же запасом в сигмах меряется
// кромка у почти решённого, и планка «зелёное только от двух сигм» обязана
// быть одной на весь экран. Реэкспорт, а не новый импорт у каждого читателя:
// панель когорты и её тесты берут z оттуда же, откуда брали всегда.
export { zTone, zWord };

/**
 * Доля крупнейшего участника в перевесе группы.
 *
 * Больше половины означает, что меряется один кит, а не когорта, — то есть
 * подсистема копирования под другим именем и с другим порогом.
 */
export function soloShare(v: string | null | undefined): string {
  const n = num(v);
  if (n === null) return "—";
  return `${Math.round(n * 100)} %`;
}

export function soloTone(v: string | null | undefined): string | undefined {
  const n = num(v);
  if (n === null) return undefined;
  return n > 0.5 ? "var(--color-bad)" : undefined;
}

/**
 * Приговор разделу одной строкой.
 *
 * Порядок ветвлений — часть смысла. Сначала называется то, что обнуляет замер
 * целиком (нечего сводить, меряется один кит), и только потом — кромка: раздел,
 * начинающийся с «кромка +2.1σ» при медианной доле кита 0.8, отправил бы
 * оператора праздновать копирование одного кошелька.
 */
export function verdict(buckets: CohortBucket[]): string {
  const settled = buckets.reduce((n, b) => n + b.settled, 0);
  const signals = buckets.reduce((n, b) => n + b.signals, 0);
  const suppressed = buckets.reduce((n, b) => n + b.suppressed, 0);
  if (signals === 0 && suppressed === 0) return "строк ещё не было";
  if (settled === 0) {
    return `${signals} зачётных, ${suppressed} погашено правилом; исходов ещё нет`;
  }

  const solo = buckets
    .map((b) => num(b.top_share_median))
    .filter((v): v is number => v !== null);
  const worstSolo = solo.length > 0 ? Math.max(...solo) : null;
  if (worstSolo !== null && worstSolo > 0.5) {
    return (
      `меряется не группа: у медианной строки ${Math.round(worstSolo * 100)} % ` +
      `перевеса даёт один кошелёк — судить надо по разрезу «с китом»`
    );
  }

  // Кромка судится по ХУДШЕМУ из двух сравнений: побить цену и не побить физику
  // означает, что куплена сторона, которая и так выигрывала.
  const zs = buckets
    .flatMap((b) => [b.price_z, b.phys_z])
    .filter((v): v is number => v !== null && v !== undefined);
  if (zs.length === 0) return `${settled} сведённых, сравнивать пока не с чем`;
  const worst = Math.min(...zs);
  const head =
    worst >= 2
      ? `кромка есть во всех разрезах, худший запас ${zWord(worst)}`
      : worst <= -2
        ? `сигнал ХУЖЕ своих знаменателей, худший ${zWord(worst)}`
        : `кромки нет: худший запас ${zWord(worst)} на ${settled} сведённых`;
  return `${head}${confirmWord(buckets)}`;
}

/**
 * Хвост приговора про подтверждение.
 *
 * Отдельной фразой, а не в общем «худшем запасе», и это осознанно: вход и
 * ожидание — РАЗНЫЕ гипотезы. Замер 2026-09-01 показал, что вся информация
 * приходит после входа, но платят за неё ушедшей ценой; смешать оба сравнения в
 * одно число значило бы потерять ровно тот вопрос, ради которого точка заведена.
 */
function confirmWord(buckets: CohortBucket[]): string {
  const grew = buckets.reduce((n, b) => n + b.grew, 0);
  if (grew === 0) return "; подтверждений с добором ещё не было";
  const zs = buckets
    .map((b) => b.grew_z)
    .filter((v): v is number => v !== null && v !== undefined);
  if (zs.length === 0) return `; ${grew} с добором, сравнивать пока не с чем`;
  return `; после добора худший запас ${zWord(Math.min(...zs))} на ${grew}`;
}

/**
 * Отравление отбором: угадано среди исполнившихся минус угадано среди всех, в
 * процентных пунктах. null — знаменателя нет.
 *
 * Отрицательное значение и есть подпись неблагоприятного отбора: нас наливают
 * охотнее там, где мы неправы.
 */
export function adverse(b: CohortBucket): number | null {
  if (b.filled <= 0 || b.settled <= 0) return null;
  return (100 * b.won_filled) / b.filled - (100 * b.won) / b.settled;
}

/** Фора до конца окна словами. Отрицательная законна: рынок живёт и после конца. */
export function lead(ms: number): string {
  const s = Math.round(ms / 1000);
  if (s < 0) return `${Math.abs(s)} с ПОСЛЕ конца`;
  if (s < 60) return `${s} с`;
  return `${Math.floor(s / 60)} мин ${s % 60} с`;
}

/**
 * Почему тейкерской цены нет, словами.
 *
 * Три РАЗНЫХ события, а не одно: из 421 строки без цены на замере 2026-09-01
 * 312 не имели аска вовсе (рыночный факт почти решённого окна), 71 не читала
 * стакан, 38 имели аск при нулевом размере прохода. Прочерк без причины
 * читается как поломка там, где это рынок.
 *
 * Незнакомая причина отдаётся КАК ЕСТЬ: показанная как «—», она выглядела бы
 * отсутствием причины.
 */
export function takerReasonWord(reason: string | null | undefined): string {
  switch (reason) {
    case undefined:
    case null:
    case "":
      return "причина не записана";
    case "no_book":
      return "стакан не читался";
    case "no_ask":
      return "предложения не было вовсе";
    case "no_size":
      return "проход по стакану не набрал ни одного лота";
    default:
      return reason;
  }
}

/**
 * Подсказка к точке подтверждения.
 *
 * Пустая точка означает «не наблюдали» (роллевер, рестарт), и это НЕ «перевес не
 * удержался»: второе — измерение, первое — его отсутствие, и путать их нельзя.
 */
export function confirmTitle(s: CohortSignal): string {
  if (!s.confirm_at) return "точку подтверждения не наблюдали: окно уехало раньше";
  const tilt = num(s.confirm_tilt_usd);
  const было = num(s.tilt_usd);
  const движение =
    tilt === null || было === null
      ? "перевес не посчитан"
      : tilt > было
        ? `перевес вырос до ${usd(s.confirm_tilt_usd)}`
        : `перевес упал до ${usd(s.confirm_tilt_usd)}`;
  if (!s.confirm_price) {
    return `${движение}; цены на тот момент нет: ${takerReasonWord(s.confirm_reason)}`;
  }
  return `${движение}; вход тогда стоил бы ${s.confirm_price} — это и есть плата за ожидание`;
}

/** Строка ленты: чем она кончилась. */
export function outcomeWord(s: CohortSignal): string {
  if (s.rule) return `вне зачёта: ${ruleWord(s.rule)}`;
  if (!s.settled_at) return "ждём исхода";
  if (!s.resolved) return "исход не подтверждён";
  return s.won ? "угадано" : "мимо";
}

/**
 * Машинное имя правила словами.
 *
 * Список закрытый и повторяет перечень подсистемы. Незнакомое имя отдаётся КАК
 * ЕСТЬ, а не прячется: новая причина, показанная как «—», выглядела бы на экране
 * как отсутствие причины.
 */
export function ruleWord(rule: string): string {
  switch (rule) {
    case "market":
      return "рынка нет в справочнике";
    case "tokens":
      return "рынок не бинарный";
    case "roster":
      return "состав когорты не собран";
    // Правила больше нет: с 2026-09-01 доля кита — разрез сводки, а не гашение.
    // Имя остаётся в списке, потому что строки с ним живут в ленте до конца
    // ретеншна, и показанное как «concentration» оно читалось бы поломкой.
    case "concentration":
      return "перевес дал один кошелёк (правило снято 2026-09-01)";
    case "gross":
      return "оборот группы ниже порога";
    case "settled":
      return "исход уже определён";
    case "late":
      return "форы до конца окна нет";
    case "book":
      return "стакана не было";
    case "price_zone":
      return "цена вне зоны";
    case "min_size":
      return "в стакане нечего брать";
    default:
      return rule;
  }
}

/** Тон строки ленты. Погашенные — приглушённые, а не красные: это не отказ. */
export function rowTone(s: CohortSignal): string | undefined {
  if (s.rule) return "var(--color-muted)";
  if (!s.resolved) return undefined;
  return s.won ? "var(--color-good)" : "var(--color-bad)";
}
